import React from "react";
import ReactDOM from "react-dom";

/**
 * ThermalReceipt
 * Renders the SAME invoice data shown in the View modal, but in a
 * compact receipt-style layout meant for thermal printing.
 *
 * IMPORTANT: this renders via a React Portal directly into document.body,
 * completely OUTSIDE your app's #root div. This is required so that during
 * print, we can hide the entire #root (sidebar, navbar, modal, everything)
 * with a simple `display: none` without accidentally hiding/clipping this
 * receipt too, since it would no longer be a descendant of #root.
 *
 * Props:
 *  - invoice: the viewInvoice object (from GET /getBill/{id})
 *  - company: the myCompany object (from GET /admin/getMyCompany)
 *  - paperSize: "58mm" | "80mm"
 */
const ThermalReceipt = ({ invoice, company, paperSize = "80mm" }) => {
  if (!invoice) return null;

  const sizeClass = paperSize === "58mm" ? "size-58mm" : "size-80mm";

  const content = (
    <div className={`thermal-receipt ${sizeClass}`} id="thermal-receipt-print-area">
      {company?.logo && (
        <img
          className="logo"
          src={`data:image/jpeg;base64,${company.logo}`}
          alt="logo"
        />
      )}

      <div className="center bold">{company?.businessName || ""}</div>
      {company?.businessAddress && (
        <div className="center">{company.businessAddress}</div>
      )}
      {company?.gstin && <div className="center">GSTIN: {company.gstin}</div>}

      <div className="divider" />

      <div>
        {invoice.billType} INVOICE #{invoice.invoicePrefix || ""}
        {invoice.invoiceNumber}
      </div>
      <div>Date: {invoice.invoiceDate}</div>
      {invoice.partyName && <div>Customer: {invoice.partyName}</div>}
      {invoice.phoneNo && <div>Phone: {invoice.phoneNo}</div>}
      <div>Payment: {invoice.isCash ? "Cash" : "Credit"}</div>

      <div className="divider" />

      <table>
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Amt</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items?.map((item, idx) => (
            <tr key={idx}>
              <td>{item.itemName}</td>
              <td>
                {item.qty}
                {item.unit ? ` ${item.unit}` : ""}
              </td>
              <td>{Number(item.totalAmount || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="divider" />

      <div className="totals-row">
        <span>Sub Total</span>
        <span>{Number(invoice.subTotal || 0).toFixed(2)}</span>
      </div>
      <div className="totals-row">
        <span>Discount</span>
        <span>{Number(invoice.totalDiscount || 0).toFixed(2)}</span>
      </div>
      <div className="totals-row">
        <span>CGST</span>
        <span>{Number(invoice.totalCgst || 0).toFixed(2)}</span>
      </div>
      <div className="totals-row">
        <span>SGST</span>
        <span>{Number(invoice.totalSgst || 0).toFixed(2)}</span>
      </div>

      <div className="divider" />

      <div className="totals-row grand-total">
        <span>Grand Total</span>
        <span>{Number(invoice.grandTotal || 0).toFixed(2)}</span>
      </div>
      <div className="totals-row">
        <span>Received</span>
        <span>{Number(invoice.amountReceived || 0).toFixed(2)}</span>
      </div>
      <div className="totals-row">
        <span>Balance</span>
        <span>{Number(invoice.balanceAmount || 0).toFixed(2)}</span>
      </div>

      <div className="divider" />
      <div className="footer-note">Thank you for your business!</div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
};

export default ThermalReceipt;